// Carrito (documento 20): vive en el celular, por tienda y por negocio, y sobrevive a cerrar la app o quedarse sin
// señal. Cada línea guarda lo necesario para mostrarse sin red (nombre, presentación y el precio que vio el cliente);
// antes de confirmar se revisa con la API (`POST /tienda/carrito`), que es la que manda.
import type { MedioCobro, UnidadBulto } from '../compartido';
import type { ItemPedido, LineaRevisada, Revision } from './tipos';

/** Tope de la API por línea. */
export const MAX_BULTOS = 10_000;

export interface LineaCarrito {
  articulo_id: string;
  bultos: number;
  nombre: string | null;
  codigo: string | null;
  unidad: UnidadBulto | null;
  unidades_por_bulto: number | null;
  categoria: string | null;
  /** El precio por bulto que vio el cliente al agregarlo (null: no se muestra o todavía no se sabe). */
  precio_visto: number | null;
  /** La foto chica del catálogo: así el carrito la muestra también sin cuenta (sin la revisión del servidor). */
  foto_mini_url?: string | null;
}

export interface OpcionesPedido {
  cobro: MedioCobro | null;
  fecha_entrega: string | null;
  observaciones: string;
}

export interface Carrito {
  lineas: LineaCarrito[];
  opciones: OpcionesPedido;
  /**
   * Clave del intento de confirmar: se reusa si se reintenta (la red cortó antes de llegar la respuesta) y se olvida
   * con cualquier cambio, porque un carrito distinto es otro pedido.
   */
  clave: string | null;
}

/** Lo que se sabe de un artículo al agregarlo (del catálogo, de lo de siempre o de un pedido anterior). */
export interface DatosArticulo {
  id: string;
  nombre?: string | null;
  codigo?: string | null;
  unidad?: UnidadBulto | null;
  unidades_por_bulto?: number | null;
  categoria?: string | null;
  precio_bulto?: number | null;
  foto_mini_url?: string | null;
}

export const carritoVacio = (): Carrito => ({
  lineas: [],
  opciones: { cobro: null, fecha_entrega: null, observaciones: '' },
  clave: null,
});

const acotar = (n: number) => Math.max(0, Math.min(MAX_BULTOS, Math.floor(Number.isFinite(n) ? n : 0)));

function lineaDe(a: DatosArticulo, bultos: number, previa?: LineaCarrito): LineaCarrito {
  return {
    articulo_id: a.id,
    bultos,
    nombre: a.nombre ?? previa?.nombre ?? null,
    codigo: a.codigo ?? previa?.codigo ?? null,
    unidad: a.unidad ?? previa?.unidad ?? null,
    unidades_por_bulto: a.unidades_por_bulto ?? previa?.unidades_por_bulto ?? null,
    categoria: a.categoria ?? previa?.categoria ?? null,
    precio_visto: a.precio_bulto !== undefined ? a.precio_bulto : (previa?.precio_visto ?? null),
    foto_mini_url: a.foto_mini_url ?? previa?.foto_mini_url ?? null,
  };
}

export const bultosDe = (c: Carrito, articuloId: string): number =>
  c.lineas.find((l) => l.articulo_id === articuloId)?.bultos ?? 0;

/** Deja la cantidad de un artículo (0 lo quita). Lo nuevo va al final; lo que ya estaba conserva su lugar. */
export function fijarBultos(c: Carrito, a: DatosArticulo, bultos: number): Carrito {
  const n = acotar(bultos);
  const i = c.lineas.findIndex((l) => l.articulo_id === a.id);
  let lineas: LineaCarrito[];
  if (n === 0) lineas = c.lineas.filter((l) => l.articulo_id !== a.id);
  else if (i === -1) lineas = [...c.lineas, lineaDe(a, n)];
  else lineas = c.lineas.map((l, j) => (j === i ? lineaDe(a, n, l) : l));
  return { ...c, lineas, clave: null };
}

export const quitar = (c: Carrito, articuloId: string): Carrito => fijarBultos(c, { id: articuloId }, 0);

/**
 * Carga varios artículos de una vez ("Agregar todo", "Repetir último pedido", "Volver a pedir"). `fijar` deja la
 * cantidad indicada (lo que ya estaba con otra cantidad queda con la nueva); `sumar` la suma a la que había.
 */
export function cargarVarios(
  c: Carrito,
  items: Array<DatosArticulo & { bultos: number }>,
  modo: 'fijar' | 'sumar' = 'fijar',
): Carrito {
  let r = c;
  for (const it of items) {
    if (it.bultos <= 0) continue;
    r = fijarBultos(r, it, modo === 'sumar' ? bultosDe(r, it.id) + it.bultos : it.bultos);
  }
  return r;
}

export function fijarOpciones(c: Carrito, o: Partial<OpcionesPedido>): Carrito {
  return { ...c, opciones: { ...c.opciones, ...o }, clave: null };
}

export interface Resumen {
  articulos: number;
  bultos: number;
  /** Con los precios que vio el cliente; null si alguno no se sabe (o la empresa no muestra precios). */
  monto: number | null;
}

export function resumen(c: Carrito): Resumen {
  let monto: number | null = 0;
  let bultos = 0;
  for (const l of c.lineas) {
    bultos += l.bultos;
    monto = monto === null || l.precio_visto === null ? null : monto + l.precio_visto * l.bultos;
  }
  return { articulos: c.lineas.length, bultos, monto: c.lineas.length ? monto : 0 };
}

export const itemsParaApi = (c: Carrito): ItemPedido[] =>
  c.lineas.map((l) => ({ articulo_id: l.articulo_id, bultos: l.bultos }));

/**
 * Completa lo que falta de cada línea con la revisión (nombre y presentación de lo cargado desde "Repetir último
 * pedido", que llega sin ellos). El precio visto no se toca: es contra lo que se compara para avisar si cambió.
 */
export function completarConRevision(c: Carrito, r: Pick<Revision, 'lineas'>): Carrito {
  const porId = new Map(r.lineas.map((l) => [l.articulo_id, l]));
  let cambio = false;
  const lineas = c.lineas.map((l) => {
    const v = porId.get(l.articulo_id);
    if (!v?.nombre) return l;
    const nueva: LineaCarrito = {
      ...l,
      nombre: v.nombre,
      codigo: v.codigo,
      unidad: v.unidad,
      unidades_por_bulto: v.unidades_por_bulto,
      // Sin precio visto (se cargó sin saberlo), el de hoy pasa a ser el visto.
      precio_visto: l.precio_visto ?? v.precio_bulto,
    };
    if (
      nueva.nombre !== l.nombre ||
      nueva.unidad !== l.unidad ||
      nueva.unidades_por_bulto !== l.unidades_por_bulto ||
      nueva.precio_visto !== l.precio_visto ||
      nueva.codigo !== l.codigo
    )
      cambio = true;
    return nueva;
  });
  return cambio ? { ...c, lineas } : c;
}

export interface CambioPrecio {
  antes: number;
  ahora: number;
}

/** Artículos cuyo precio de hoy no es el que vio el cliente. */
export function cambiosDePrecio(c: Carrito, r: Pick<Revision, 'lineas'>): Map<string, CambioPrecio> {
  const salida = new Map<string, CambioPrecio>();
  const porId = new Map(r.lineas.map((l) => [l.articulo_id, l]));
  for (const l of c.lineas) {
    const v = porId.get(l.articulo_id);
    if (l.precio_visto !== null && v?.precio_bulto != null && v.precio_bulto !== l.precio_visto)
      salida.set(l.articulo_id, { antes: l.precio_visto, ahora: v.precio_bulto });
  }
  return salida;
}

/** Acepta el precio de hoy de un artículo (al tocar su cantidad o al confirmar que lo vio). */
export function aceptarPrecio(c: Carrito, articuloId: string, precio: number | null): Carrito {
  return {
    ...c,
    lineas: c.lineas.map((l) => (l.articulo_id === articuloId ? { ...l, precio_visto: precio } : l)),
  };
}

export type Arreglo = { tipo: 'quitar' } | { tipo: 'ajustar'; bultos: number } | null;

/** Qué se puede hacer con una línea con problema: quitarla o dejar lo que hay. */
export function arregloDe(l: Pick<LineaRevisada, 'problema' | 'stock_bultos'>): Arreglo {
  if (!l.problema) return null;
  if (l.problema === 'stock_insuficiente' && l.stock_bultos && l.stock_bultos > 0)
    return { tipo: 'ajustar', bultos: l.stock_bultos };
  return { tipo: 'quitar' };
}

/** Arregla todas las líneas con problema de una vez. */
export function arreglarTodo(c: Carrito, r: Pick<Revision, 'lineas'>): Carrito {
  let salida = c;
  for (const l of r.lineas) {
    const a = arregloDe(l);
    if (!a) continue;
    salida = a.tipo === 'quitar' ? quitar(salida, l.articulo_id) : fijarBultos(salida, { id: l.articulo_id }, a.bultos);
  }
  return salida;
}

/** Lee un carrito guardado, descartando lo que no tenga forma de carrito (versión vieja o datos rotos). */
export function carritoValido(v: unknown): Carrito {
  const c = v as Partial<Carrito> | null;
  if (!c || !Array.isArray(c.lineas)) return carritoVacio();
  const lineas = c.lineas.filter(
    (l): l is LineaCarrito => !!l && typeof l.articulo_id === 'string' && typeof l.bultos === 'number' && l.bultos > 0,
  );
  return {
    lineas: lineas.map((l) => ({ ...l, bultos: acotar(l.bultos) })),
    opciones: { ...carritoVacio().opciones, ...(c.opciones ?? {}) },
    clave: typeof c.clave === 'string' ? c.clave : null,
  };
}

// ---------- Carrito del visitante (decisión D49) ----------

export interface ResumenUnion {
  /** Productos que se eligieron sin cuenta. */
  traidos: number;
  /** Productos que el negocio ya tenía guardados de antes (y siguen en el carrito). */
  previos: number;
}

/**
 * Junta lo elegido sin cuenta con el carrito que el negocio ya tenía guardado: en lo repetido manda lo recién elegido
 * (cantidad y precio que vio), lo demás se suma al final. Las opciones (pago, nota) quedan las del negocio y, si no
 * tenía, las del visitante.
 */
export function unirCarritos(guardado: Carrito, visita: Carrito): { carrito: Carrito; resumen: ResumenUnion } {
  const deVisita = new Map(visita.lineas.map((l) => [l.articulo_id, l]));
  const previos = guardado.lineas.filter((l) => !deVisita.has(l.articulo_id));
  const g = guardado.opciones;
  const v = visita.opciones;
  return {
    carrito: {
      lineas: [...previos, ...visita.lineas],
      opciones: {
        cobro: g.cobro ?? v.cobro,
        fecha_entrega: g.fecha_entrega ?? v.fecha_entrega,
        observaciones: g.observaciones || v.observaciones,
      },
      clave: null,
    },
    resumen: { traidos: visita.lineas.length, previos: previos.length },
  };
}
